import React, { useState, useEffect } from 'react';
import { PlusCircle, ShoppingCart, Calendar, Trash2, AlertCircle } from 'lucide-react';
import { Recipe, GroceryItem } from '../types';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { startOfDay, isAfter } from 'date-fns';

interface GroceryListProps {
  recipes: Recipe[];
  onItemsChange?: () => void;
}

export default function GroceryList({ recipes, onItemsChange }: GroceryListProps) {
  const { user } = useAuth();
  const [groceryItems, setGroceryItems] = useState<GroceryItem[]>([]);
  const [newItem, setNewItem] = useState({ name: '', amount: 1, unit: '' });
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      loadGroceryItems();
    }
  }, [user]);

  const loadGroceryItems = async () => {
    try {
      const { data, error } = await supabase
        .from('grocery_items')
        .select(`
          id,
          name,
          amount,
          unit,
          checked,
          recipe_id,
          created_at
        `)
        .eq('user_id', user?.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      const items = data.map(item => ({
        ...item,
        recipeId: item.recipe_id
      }));
      
      setGroceryItems(items);
      onItemsChange?.();
    } catch (error) {
      console.error('Error loading grocery items:', error);
      toast.error('Failed to load grocery list');
    } finally {
      setLoading(false);
    }
  };

  const handleAddManualItem = async () => {
    if (newItem.name) {
      try {
        const { error } = await supabase
          .from('grocery_items')
          .insert({
            name: newItem.name,
            amount: newItem.amount,
            unit: newItem.unit,
            user_id: user?.id,
          });

        if (error) throw error;

        toast.success('Item added to grocery list');
        await loadGroceryItems();
        setNewItem({ name: '', amount: 1, unit: '' });
      } catch (error) {
        console.error('Error adding grocery item:', error);
        toast.error('Failed to add item');
      }
    }
  };

  const handleToggleItem = async (id: string) => {
    try {
      const item = groceryItems.find((i) => i.id === id);
      const { error } = await supabase
        .from('grocery_items')
        .update({ checked: !item?.checked })
        .eq('id', id);

      if (error) throw error;
      await loadGroceryItems();
    } catch (error) {
      console.error('Error updating grocery item:', error);
      toast.error('Failed to update item');
    }
  };

  const handleAddFromRecipe = async (recipe: Recipe) => {
    try {
      const newItems = recipe.ingredients.map((ingredient) => ({
        name: ingredient.name,
        amount: ingredient.amount,
        unit: ingredient.unit,
        recipe_id: recipe.id,
        user_id: user?.id,
      }));

      const { error } = await supabase
        .from('grocery_items')
        .insert(newItems);

      if (error) throw error;

      toast.success('Recipe ingredients added to grocery list');
      await loadGroceryItems();
    } catch (error) {
      console.error('Error adding recipe ingredients:', error);
      toast.error('Failed to add recipe ingredients');
    }
  };

  const handleResetList = async () => {
    try {
      setResetting(true);
      const { error } = await supabase
        .from('grocery_items')
        .delete()
        .eq('user_id', user?.id);

      if (error) throw error;

      setGroceryItems([]);
      toast.success('Grocery list has been cleared');
      onItemsChange?.();
      setShowResetConfirm(false);
    } catch (error) {
      console.error('Error resetting grocery list:', error);
      toast.error('Failed to clear grocery list');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold">Grocery List</h2>
        <button
          onClick={() => setShowResetConfirm(true)}
          disabled={resetting || groceryItems.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Trash2 className="h-5 w-5" />
          Clear List
        </button>
      </div>

      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h3 className="text-lg font-medium mb-4">Add Manual Item</h3>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newItem.name}
            onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
            placeholder="Item name"
            className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <input
            type="number"
            value={newItem.amount}
            onChange={(e) => setNewItem({ ...newItem, amount: parseInt(e.target.value) })}
            placeholder="Amount"
            className="w-24 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <input
            type="text"
            value={newItem.unit}
            onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
            placeholder="Unit"
            className="w-24 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <button
            onClick={handleAddManualItem}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <PlusCircle className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h3 className="text-lg font-medium mb-4">Add from Recipe</h3>
        <div className="space-y-2">
          {recipes.map((recipe) => (
            <button
              key={recipe.id}
              onClick={() => handleAddFromRecipe(recipe)}
              className="w-full text-left px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              {recipe.name}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Shopping List</h3>
        {groceryItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ShoppingCart className="h-12 w-12 mx-auto mb-2" />
            <p>Your shopping list is empty</p>
          </div>
        ) : (
          <div className="space-y-2">
            {groceryItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-4 p-2 hover:bg-gray-50 rounded transition-colors"
              >
                <input
                  type="checkbox"
                  checked={item.checked}
                  onChange={() => handleToggleItem(item.id)}
                  className="h-5 w-5 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                />
                <span className={item.checked ? 'line-through text-gray-400' : ''}>
                  {item.amount} {item.unit} {item.name}
                </span>
                {item.recipeId && (
                  <span className="text-sm text-gray-500">
                    (from {recipes.find((r) => r.id === item.recipeId)?.name})
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Clear Grocery List</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <p className="mb-6 text-gray-600">
              Are you sure you want to clear your entire grocery list? All items will be permanently removed.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetList}
                disabled={resetting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {resetting ? 'Clearing...' : 'Clear List'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}